import React, { useState, useEffect, useMemo } from 'react'
import { ButtonAddHLPure, DialogYesNoHLPure, DropDownBtnHLPure, GridSelectionHLPure, SearchBarHLPure, SimpleLoading, LabelModalPure, DialogSystemGLPure } from '../../pures';
import { filterBy } from '@progress/kendo-data-query';
import { Container, Grid, useMediaQuery } from '@material-ui/core';
import DigitalVaultDocumentAction from '../../actions/digital-vault-document/digital-vault-document.action';
import { useTranslation } from 'react-i18next';
import './digital-vault-bin.css'

const HeaderColumns = (props) => {
  return <span className={(props.title === props.t('digitalVaultBinView.grid.title.1')) ? 'grid-header-one' : 'grid-headers'}>{props.title}</span>;
};

const CellColumnName = (props) => {
  return (
    <td>
      <div className='grid-cell-double-container-margin-left'>
        <p className='grid-cell-titles'>{props.dataItem.name}</p>
        <p className='grid-cell-subtitles'>{props.dataItem.companyName}</p>
      </div>
    </td>
  )
}

const CellColumnDate = (props) => {
  return (
    <td>
      <div>
        <p className={'grid-cell-subtitles'}>{props.dataItem.deletedDate}</p>
      </div>
    </td>
  )
}

const DigitalVaultBinView = (props) => {
  const {
    isLoading,
    companies,
    selectedCompanyId,
    setSelectedCompanyId,
    documents,
    selectedDocument,
    setSelectedDocument,
    viewDeleteDialog,
    setViewDeleteDialog,
    deleteMany,
    deleteDocument,
    restoreDocument,
    isDeletingDocument = false
  } = props;

  const { t } = useTranslation()
  const [skip, setSkip] = useState(0);

  const [documentsDatagrid, setDocumentsDatagrid] = useState(documents);
  const [documentSearch, setDocumentSearch] = useState('');

  useEffect(() => {
    let filter = {
      logic: 'and',
      filters: [
        {
          field: 'name',
          operator: 'contains',
          value: documentSearch,
        }
      ],
    }
    if (selectedCompanyId !== '0') {
      filter.filters.push({
        field: 'companyId',
        operator: 'eq',
        value: selectedCompanyId,
      })
    }
    const newData = filterBy(documents, filter);
    setDocumentsDatagrid(newData);
    setSkip(0);
  }, [documents, documentSearch, selectedCompanyId]);

  const onCompanyDropdownChange = (e) => setSelectedCompanyId(e.target.value)

  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
  const [viewDocument, setViewDocument] = useState(false);
  const [fromDeleteFlag, setFromDeleteFlag] = useState(false)
  const [suppressDeletingInfo, setSuppressDeletingInfo] = useState(false)
  const [gridAction, setGridAction] = useState('');
  const [hashKey, setHashKey] = useState(0)
  const [showDeletingInfoModal, setShowDeletingInfoModal] = useState(false)


  const dataGridWithFlags = useMemo(() => {
    return (documentsDatagrid || []).map(d => {
      const inDeletingFlow = !!d?.isDeletingDocument
      return {
        ...d,
        iconTrashActive: true,
        disableFirst: inDeletingFlow,
        disableTrash: inDeletingFlow,
        tooltipFirstText: inDeletingFlow
          ? t('digitalVaultBinView.tooltip.deletingFlow')
          : undefined,
        tooltipTrashIcon: inDeletingFlow
          ? t('digitalVaultBinView.tooltip.deletingFlow')
          : t('digitalVaultBinView.grid.btns.delete.permanently'),
      }
    })
  }, [documentsDatagrid, t])


  const handleCloseDeleteConfirmModal = () => {
    setViewDeleteDialog(false);
    setGridAction('');
  };

  const desktop = useMediaQuery('(min-width:1020px)')
  const is1500 = useMediaQuery('(max-width: 1500px)')
  const is1280 = useMediaQuery('(max-width: 1280px)')
  const is960 = useMediaQuery('(max-width: 960px)')
  const showMobile = useMediaQuery('(max-width:721px)')
  const is560 = useMediaQuery('(max-width:560px)')

  useEffect(() => {
    const onHashChange = () => setHashKey(k => k + 1)
    window.addEventListener('hashchange', onHashChange)
    return () => window.removeEventListener('hashchange', onHashChange)
  }, []);

  useEffect(() => {
    if (gridAction === '' || !selectedDocument || !selectedDocument.id) return;

    const docIsDeleting = selectedDocument?.isDeletingDocument ?? isDeletingDocument

    if (gridAction === t('digitalVaultBinView.grid.btns.view.document')) {
      setViewDocument(true)
      if (docIsDeleting) setShowDeletingInfoModal(true)
      setGridAction('')
    } else if (gridAction === t('digitalVaultBinView.grid.btns.restore')) {
      if (!docIsDeleting) restoreDocument()
      setGridAction('')
    } else if (gridAction === t('digitalVaultBinView.grid.btns.delete')) {
      if (!docIsDeleting) setViewDeleteDialog(true)
      setGridAction('')
    }
  }, [selectedDocument, gridAction, t, restoreDocument, setViewDeleteDialog, isDeletingDocument])

  useEffect(() => {
    const hash = window.location.hash || ''
    const queryString = hash.includes('?') ? hash.split('?')[1] : ''
    if (!queryString) return

    const params = new URLSearchParams(queryString);
    const decodeParam = (v) => (v ? decodeURIComponent(v.replace(/\+/g, ' ')) : v)

    const documentIdParam = params.get('documentId')
    const expedienteIdParam = params.get('expedienteId')
    const fromDeleteParam = params.get('fromDelete')
    const autoOpen = params.get('autoOpen') === 'true'
    const allowPermanent = params.get('allowPermanent') === 'true'
    const rawNameParam = params.get('name')
    const nameParam = decodeParam(rawNameParam)
    const companyIdParam = params.get('companyId')

    if (fromDeleteParam === 'true') setFromDeleteFlag(true)
    if (allowPermanent) setSuppressDeletingInfo(true)

    const idToUse = documentIdParam || expedienteIdParam
    if (!idToUse && !nameParam && !autoOpen) return

    let docToOpen = null
    if (idToUse) {
      const found = (documents || []).find(d => String(d.id) === String(idToUse))
      docToOpen = found || { id: idToUse }
    }

    if (!docToOpen && nameParam) {
      const pool = (documents || [])
      const normalize = (s) => (s || '')
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()
      const slug = (s) => normalize(s).replace(/[^a-z0-9]/g, '')

      const normalizedName = normalize(nameParam)
      const nameSlug = slug(nameParam)
      if (companyIdParam && typeof setSelectedCompanyId === 'function') {
        setSelectedCompanyId(companyIdParam)
      }

      let candidates = pool.filter(d => normalize(d.name) === normalizedName)
      if (candidates.length === 0) {
        candidates = pool.filter(d => slug(d.name) === nameSlug)
      }
      if (candidates.length === 0) {
        candidates = pool.filter(d => normalize(d.name).includes(normalizedName))
      }

      let chosen = candidates[0]
      if (companyIdParam && candidates.length > 1) {
        const byCompany = candidates.find(d => String(d.companyId) === String(companyIdParam))
        if (byCompany) chosen = byCompany
      }

      if (chosen) docToOpen = chosen
    }

    if (!docToOpen && autoOpen && (documents || []).length > 0) {
      docToOpen = documents[0]
    }
    if (!docToOpen) return

    setSelectedDocument(docToOpen)
    setViewDocument(true)
    if (docToOpen?.isDeletingDocument && !suppressDeletingInfo) setShowDeletingInfoModal(true)
  }, [documents, setSelectedDocument, setSelectedCompanyId, hashKey, isDeletingDocument, suppressDeletingInfo])

  if (isLoading) return <SimpleLoading />

  if (viewDocument && selectedDocument) {
    return <DigitalVaultDocumentAction
      documentId={selectedDocument.id}
      fromDelete={fromDeleteFlag}
      isDeletingDocument={selectedDocument?.isDeletingDocument ?? isDeletingDocument}
      onShowDeletingInfo={() => setShowDeletingInfoModal(true)}
      goBack={() => {
        const index = documentsDatagrid.findIndex(doc => doc.id === selectedDocument.id);
        setSkip(Math.floor(index / 6) * 6);
        setSelectedDocument(null);
        setViewDocument(false);
      }}
    />
  }

  let AllHeadersColumns = (props) => <HeaderColumns {...props} t={t} />
  let CellColumnOne = (props) => <CellColumnName {...props} t={t} />
  let CellColumnTwo = (props) => <CellColumnDate {...props} t={t} />

  let widthCellOne = '200px'
  let widthCellTwo = '100px'
  let widthCellFive = '120px'

  if (is1500) widthCellFive = '140px'
  if (is1280) widthCellFive = '200px'
  if (is960) {
    widthCellOne = '150px'
    widthCellFive = '200px'
  }

  if (showMobile) {
    widthCellOne = '100px'
    widthCellFive = '50px'
  }

  if (is560) {
    widthCellOne = '200px'
    widthCellFive = '60px'
  }

  return (
    <div className='digitalVaultBinViewContainer'>
      <Container disableGutters className='digitalVaultBinView'>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div className='digitalVaultBinView-filters'>
              <ButtonAddHLPure
                nameBtn={t('digitalVaultBinView.btn.delete.all')}
                onClickBtn={() => setShowDeleteAllDialog(true)}
              />
              <SearchBarHLPure
                placeholder={t('digitalVaultBinView.placeholder.search.document')}
                searchValue={documentSearch}
                onSearchChange={(e) => setDocumentSearch(e.target.value)}
              />
              <DropDownBtnHLPure
                optionsDropDown={companies}
                optionsValue={selectedCompanyId}
                valueItem={'id'}
                nameOption={'companyName'}
                onDropDownChange={onCompanyDropdownChange}
                iconFilter={desktop}
              />
            </div>
          </Grid>
          <Grid item xs={12} className='digitalVaultBinView-grid'>
            <GridSelectionHLPure
              skip={skip}
              take={6}
              pageable
              dataGrid={dataGridWithFlags}

              titleRow1={t('digitalVaultBinView.grid.title.1')}
              headerColumnOne={AllHeadersColumns}
              cellColumnOne={CellColumnOne}
              widthColumnOne={widthCellOne}
              minResizableWidth1={'auto'}

              columnTwo={!is560}
              titleRow2={t('digitalVaultBin.grid.title.2')}
              headerColumnTwo={AllHeadersColumns}
              cellColumnTwo={CellColumnTwo}
              widthColumnTwo={widthCellTwo}
              minResizableWidth2={widthCellTwo}

              columnFive
              columnFiveStyleClass={'digitalVaultBinView-cellActions'}
              titleRow5={t('digitalVaultBinView.grid.title.4')}

              // Botón Restaurar
              iconOneActive={!showMobile}
              labelOneActions={is560 ? '' : t('digitalVaultBinView.grid.btns.restore')}
              disableFirst={isDeletingDocument}
              tooltipOneText={
                isDeletingDocument ? t('digitalVaultBinView.tooltip.deletingFlow') : undefined
              }

              // Botón Ver Documento
              iconFourthActive={!showMobile}
              labelFourthActions={
                !showMobile
                  ? t('digitalVaultBinView.grid.btns.view.document')
                  : t('digitalVaultBinView.grid.btns.view.document')
              }

              // Botón Eliminar
              iconTrashActive
              disableTrash={isDeletingDocument}
              labelTrashActions={t('digitalVaultBinView.grid.btns.delete')}
              tooltipTrashIcon={
                isDeletingDocument
                  ? t('digitalVaultBinView.tooltip.deletingFlow')
                  : t('digitalVaultBinView.grid.btns.delete.permanently')
              }
              widthColumnFive={widthCellFive}
              minResizableWidth5={widthCellFive}

              onSelectedItem={setSelectedDocument}
              setNameComponentUser={setGridAction}
              setShowDetails={() => { /* no ocupo */ }}
            />
          </Grid>
        </Grid>
      </Container>
      {
        viewDeleteDialog && (
          <DialogSystemGLPure
            showConfirm={true}
            confirmTitle={t('dialogSystemGLPure.confirmDeleteFlow.title')}
            confirmMessage={t('dialogSystemGLPure.confirmDeleteFlow.message')}
            labelCancelConfirmation={t('button.cancel')}
            labelSubmitConfirmation={t('dialogSystemGLPure.confirmDeleteFlow.submit')}
            handleConfirmDeleteFlowClose={handleCloseDeleteConfirmModal}
            handleConfirmDeleteFlowSubmit={deleteDocument}
          />
        )
      }
      {
        showDeleteAllDialog &&
        <DialogSystemGLPure
          showConfirm={true}
          confirmTitle={t('digitalVaultBinView.modal.all.title')}
          confirmMessage={t('digitalVaultBinView.modal.all.question')}
          labelCancelConfirmation={t('button.cancel')}
          handleConfirmDeleteFlowClose={() => {
            setShowDeleteAllDialog(false)
          }}
          labelSubmitConfirmation={t('button.delete')}
          handleConfirmDeleteFlowSubmit={() => {
            setShowDeleteAllDialog(false)
            deleteMany(documentsDatagrid.map(d => d.id))
          }}
        />
      }
    </div>
  )
}

export default DigitalVaultBinView